/**
 * AI Roadmap Generator Component
 * 
 * Provides a user interface for generating roadmaps using AI
 * with natural language descriptions.
 */

import React, { useState } from 'react';
import { useLanguage } from '../../state/language/LanguageProvider';


interface RoadmapGenerationResponse {
  roadmap: {
    id: string;
    name: string;
    description: string;
    projects: Array<{
      id: string;
      name: string;
      description: string;
      timeline: string;
      dependencies: string[];
      kpis: string[];
    }>;
    timeline: string;
    totalBudget: string;
  };
  context: Array<{
    content: string;
    similarity: number;
    source: string;
  }>;
  usage: {
    model: string;
    inputTokens: number;
    outputTokens: number;
    cost: number;
    responseTime: number;
  };
}

export const AIRoadmapGenerator: React.FC = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'generic' | 'problem-solver'>('generic');
  
  // Generic roadmap generation state
  const [description, setDescription] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState<RoadmapGenerationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState('ollama');
  const [isSaving, setIsSaving] = useState(false);

  // Business problem solver state
  const [businessContext, setBusinessContext] = useState({
    industry: '',
    companySize: '',
    revenue: '',
    employees: '',
    currentChallenges: '',
    goals: '',
    timeline: '',
    budget: ''
  });
  const [conversationStep, setConversationStep] = useState<'input' | 'conversation' | 'suggestions' | 'roadmap'>('input');
  const [currentQuestion, setCurrentQuestion] = useState<string>('');
  const [userAnswer, setUserAnswer] = useState<string>('');
  const [conversationHistory, setConversationHistory] = useState<Array<{
    type: 'ai' | 'user';
    content: string;
    timestamp: Date;
  }>>([]);
  const [suggestedOptions, setSuggestedOptions] = useState<{
    summary: string;
    recommendations: string[];
    readyToBuild: boolean;
  } | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGeneratingResponse, setIsGeneratingResponse] = useState(false);

  const handleGenerate = async () => {
    if (!description.trim()) {
      setError(t('ai.roadmapGenerator.errorMessages.enterDescription'));
      return;
    }

    setIsGenerating(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch('/api/v1/ai/roadmap/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          description: description.trim(),
          modelName: selectedModel,
          includeContext: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('ai.roadmapGenerator.errorMessages.failedToGenerate'));
      }

      const data = await response.json();
      if (data.success && data.data) {
        setResult(data.data);
      } else {
        throw new Error(data.error || t('ai.roadmapGenerator.errorMessages.failedToGenerate'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('ai.roadmapGenerator.errorMessages.anErrorOccurred'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveRoadmap = async (location: 'tenant' | 'marketplace') => {
    if (!result) return;

    setIsSaving(true);
    setError(null);

    try {
      const response = await fetch('/api/v1/ai/roadmap/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          roadmap: result.roadmap,
          saveLocation: location,
          originalDescription: description
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('ai.roadmapGenerator.errorMessages.failedToSave'));
      }

      setError(null);
      // Show success message
      alert(location === 'tenant' ? t('ai.roadmapGenerator.roadmap.savedToMyRoadmaps') : t('ai.roadmapGenerator.roadmap.savedToMarketplace'));
      
    } catch (err) {
      setError(err instanceof Error ? err.message : t('ai.roadmapGenerator.errorMessages.failedToSave'));
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!description.trim()) {
      setError(t('ai.roadmapGenerator.errorMessages.enterDescription'));
      return;
    }

    try {
      const response = await fetch('/api/v1/ai/roadmap/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          description: description.trim(),
          modelName: selectedModel
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('ai.roadmapGenerator.errorMessages.failedToPreview'));
      }

      await response.json();
      // You could show a preview modal here
    } catch (err) {
      setError(err instanceof Error ? err.message : t('ai.roadmapGenerator.errorMessages.anErrorOccurred'));
    }
  };

  // Business Problem Solver handlers
  const handleStartConversation = async () => {
    if (!businessContext.currentChallenges.trim()) {
      setError(t('ai.roadmapGenerator.businessProblemSolver.errors.challengesRequired'));
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setConversationStep('conversation');

    try {
      const response = await fetch('/api/v1/ai/roadmap/start-conversation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          businessContext,
          modelName: selectedModel
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('ai.roadmapGenerator.businessProblemSolver.errors.failedToStartConversation'));
      }

      const data = await response.json();
            
      if (data.success && data.data) {
        const aiMessage = data.data.message;
        setCurrentQuestion(aiMessage);
        setConversationHistory([{
          type: 'ai',
          content: aiMessage,
          timestamp: new Date()
        }]);
      } else {
        throw new Error(data.message || t('ai.roadmapGenerator.businessProblemSolver.errors.failedToStartConversation'));
      }
    } catch (err) {
      console.error('Conversation start error:', err);
      setError(err instanceof Error ? err.message : t('ai.roadmapGenerator.businessProblemSolver.errors.anErrorOccurredWhileStarting'));
      setConversationStep('input');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSendMessage = async () => {
    if (!userAnswer.trim() || !currentQuestion) return;

    setIsGeneratingResponse(true);
    
    // Add user message to conversation history
    const userMessage = {
      type: 'user' as const,
      content: userAnswer.trim(),
      timestamp: new Date()
    };
    
    setConversationHistory(prev => [...prev, userMessage]);
    setUserAnswer('');

    try {
      const response = await fetch('/api/v1/ai/roadmap/continue-conversation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          businessContext,
          conversationHistory: [...conversationHistory, userMessage],
          modelName: selectedModel
        })
      });

      if (!response.ok) {
        throw new Error(t('ai.roadmapGenerator.businessProblemSolver.errors.aiResponseFailed'));
      }

      const data = await response.json();
            
      if (data.success && data.data) {
        const aiResponse = data.data;
        
        if (aiResponse.type === 'suggestions') {
          // AI is ready to suggest options
          setSuggestedOptions({
            summary: aiResponse.summary,
            recommendations: aiResponse.recommendations,
            readyToBuild: aiResponse.readyToBuild
          });
          setConversationStep('suggestions');
        } else {
          // AI continues asking questions
          setCurrentQuestion(aiResponse.message);
          setConversationHistory(prev => [...prev, {
            type: 'ai',
            content: aiResponse.message,
            timestamp: new Date()
          }]);
        }
      } else {
        throw new Error(data.message || t('ai.roadmapGenerator.businessProblemSolver.errors.aiResponseFailed'));
      }
    } catch (err) {
      console.error('AI response error:', err);
      setError(t('ai.roadmapGenerator.businessProblemSolver.errors.aiResponseFailed'));
    } finally {
      setIsGeneratingResponse(false);
    }
  };

  const handleBuildProjects = async () => {
    setIsGenerating(true);
    setConversationStep('roadmap');

    try {
      const response = await fetch('/api/v1/ai/roadmap/generate-from-conversation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          businessContext,
          conversationHistory,
          suggestedOptions,
          modelName: selectedModel
        })
      });

      if (!response.ok) {
        throw new Error(t('ai.roadmapGenerator.businessProblemSolver.errors.roadmapGenerationFailed'));
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error('Roadmap generation error:', err);
      setError(t('ai.roadmapGenerator.businessProblemSolver.errors.roadmapGenerationFailed'));
    } finally {
      setIsGenerating(false);
    }
  };


  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[var(--color-surface-text)] mb-2">
          {t('ai.roadmapGenerator.title')}
        </h2>
        <p className="text-[var(--color-surface-text)] opacity-80">
          {t('ai.roadmapGenerator.description')}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-[var(--color-border)]">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('generic')}
              className={`py-2 px-4 border-b-2 font-medium text-sm rounded-t-md ${
                activeTab === 'generic'
                  ? 'border-[var(--color-primary-bg)] bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]'
                  : 'border-transparent text-[var(--color-surface-text)] hover:text-[var(--color-primary-bg)] hover:border-[var(--color-border)] hover:bg-gray-50'
              }`}
            >
              {t('ai.roadmapGenerator.tabs.genericGenerator')}
            </button>
            <button
              onClick={() => setActiveTab('problem-solver')}
              className={`py-2 px-4 border-b-2 font-medium text-sm rounded-t-md ${
                activeTab === 'problem-solver'
                  ? 'border-[var(--color-primary-bg)] bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]'
                  : 'border-transparent text-[var(--color-surface-text)] hover:text-[var(--color-primary-bg)] hover:border-[var(--color-border)] hover:bg-gray-50'
              }`}
            >
              {t('ai.roadmapGenerator.tabs.businessProblemSolver')}
            </button>
          </nav>
        </div>
      </div>

      {/* Model Selection */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
          {t('ai.roadmapGenerator.genericGenerator.modelSelection')}
        </label>
        <select
          value={selectedModel}
          onChange={(e) => setSelectedModel(e.target.value)}
          className="w-full p-3 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-primary-bg)] focus:border-transparent"
        >
          <option value="ollama">{t('ai.roadmapGenerator.modelOptions.ollama')}</option>
          <option value="openai">{t('ai.roadmapGenerator.modelOptions.openai')}</option>
          <option value="claude">{t('ai.roadmapGenerator.modelOptions.claude')}</option>
        </select>
      </div>

      {/* Tab Content */}
      {activeTab === 'generic' && (
        <div>
          {/* Generic Roadmap Generator Content */}

      {/* Description Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
          {t('ai.roadmapGenerator.genericGenerator.description')}
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('ai.roadmapGenerator.genericGenerator.descriptionPlaceholder')}
          className="w-full p-4 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-primary-bg)] focus:border-transparent h-48 resize-y"
          disabled={isGenerating}
        />
        <div className="text-sm text-[var(--color-surface-text)] opacity-70 mt-1">
          {description.length}/10000 {t('ai.roadmapGenerator.characterCount')}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={handlePreview}
          disabled={isGenerating || !description.trim()}
          className="px-6 py-2 bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {t('ai.roadmapGenerator.preview')}
        </button>
        <button
          onClick={handleGenerate}
          disabled={isGenerating || !description.trim()}
          className="px-6 py-2 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isGenerating ? t('ai.roadmapGenerator.genericGenerator.generating') : t('ai.roadmapGenerator.genericGenerator.generateButton')}
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <span className="text-red-500 text-xl">⚠️</span>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {t('ai.roadmapGenerator.errorMessages.aiServiceError')}
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
                {error.includes('timeout') && (
                  <div className="mt-2 p-2 bg-red-100 rounded">
                    <p className="font-medium">{t('ai.roadmapGenerator.errorMessages.timeoutHelp.title')}</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>{t('ai.roadmapGenerator.errorMessages.timeoutHelp.waitAndRetry')}</li>
                      <li>{t('ai.roadmapGenerator.errorMessages.timeoutHelp.modelLoading')}</li>
                      <li>{t('ai.roadmapGenerator.errorMessages.timeoutHelp.simplerDescription')}</li>
                    </ul>
                  </div>
                )}
                {error.includes('not running') && (
                  <div className="mt-2 p-2 bg-red-100 rounded">
                    <p className="font-medium">{t('ai.roadmapGenerator.errorMessages.notRunningHelp.title')}</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>{t('ai.roadmapGenerator.errorMessages.notRunningHelp.checkOllama')}</li>
                      <li>{t('ai.roadmapGenerator.errorMessages.notRunningHelp.restartOllama')}</li>
                      <li>{t('ai.roadmapGenerator.errorMessages.notRunningHelp.contactAdmin')}</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isGenerating && (
        <div className="mb-6 p-6 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-[var(--color-primary-bg)]"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-bold text-[var(--color-primary-bg)]">⏰</span>
                </div>
              </div>
              <div className="text-center">
                <p className="text-[var(--color-primary-text)] font-semibold text-lg">
                  {t('ai.roadmapGenerator.loadingStates.aiThinking')}
                </p>
                <p className="text-[var(--color-primary-text)] opacity-80 text-sm mt-1">
                  {t('ai.roadmapGenerator.loadingStates.thinkingDescription')}
                </p>
                <div className="mt-2 text-xs text-[var(--color-surface-text)] opacity-70">
                  <div className="flex items-center justify-center space-x-4">
                    <span>{t('ai.roadmapGenerator.loadingStates.loadingSteps.loadingModel')}</span>
                    <span>{t('ai.roadmapGenerator.loadingStates.loadingSteps.processingRequest')}</span>
                    <span>{t('ai.roadmapGenerator.loadingStates.loadingSteps.generatingRoadmap')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Display */}
      {result && (
        <div className="space-y-6">
          {/* Roadmap Overview */}
          <div className="p-6 bg-green-50 border border-green-200 rounded-md">
            <h3 className="text-xl font-bold text-[var(--color-surface-text)] mb-4">
              {result.roadmap.name}
            </h3>
            <p className="text-[var(--color-surface-text)] mb-4">{result.roadmap.description}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-[var(--color-surface-bg)] p-3 rounded border border-[var(--color-border)]">
                <div className="text-sm text-[var(--color-surface-text)] opacity-70">{t('ai.roadmapGenerator.roadmapDisplay.timeline')}</div>
                <div className="font-semibold text-[var(--color-surface-text)]">{result.roadmap.timeline}</div>
              </div>
              <div className="bg-[var(--color-surface-bg)] p-3 rounded border border-[var(--color-border)]">
                <div className="text-sm text-[var(--color-surface-text)] opacity-70">{t('ai.roadmapGenerator.roadmapDisplay.budget')}</div>
                <div className="font-semibold text-[var(--color-surface-text)]">{result.roadmap.totalBudget}</div>
              </div>
              <div className="bg-[var(--color-surface-bg)] p-3 rounded border border-[var(--color-border)]">
                <div className="text-sm text-[var(--color-surface-text)] opacity-70">{t('ai.roadmapGenerator.roadmapDisplay.projects')}</div>
                <div className="font-semibold text-[var(--color-surface-text)]">{result.roadmap.projects.length}</div>
              </div>
            </div>
          </div>

          {/* Projects */}
          <div>
            <h4 className="text-lg font-bold text-[var(--color-surface-text)] mb-4">
              {t('ai.roadmapGenerator.projects')}
            </h4>
            <div className="space-y-4">
              {result.roadmap.projects.map((project) => (
                <div key={project.id} className="p-4 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)]">
                  <h5 className="font-semibold text-[var(--color-surface-text)] mb-2">{project.name}</h5>
                  <p className="text-[var(--color-surface-text)] mb-3">{project.description}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-[var(--color-surface-text)]">{t('ai.roadmapGenerator.roadmapDisplay.projectTimeline')}</span> <span className="text-[var(--color-surface-text)] opacity-80">{project.timeline}</span>
                    </div>
                    <div>
                      <span className="font-medium text-[var(--color-surface-text)]">{t('ai.roadmapGenerator.roadmapDisplay.projectDependencies')}</span> <span className="text-[var(--color-surface-text)] opacity-80">{project.dependencies.length > 0 ? project.dependencies.join(', ') : t('ai.roadmapGenerator.roadmapDisplay.noDependencies')}</span>
                    </div>
                  </div>
                  <div className="mt-3">
                    <span className="font-medium text-sm text-[var(--color-surface-text)]">{t('ai.roadmapGenerator.roadmapDisplay.projectKPIs')}</span>
                    <ul className="list-disc list-inside text-sm text-[var(--color-surface-text)] opacity-80 mt-1">
                      {project.kpis.map((kpi, index) => (
                        <li key={index}>{kpi}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Save Options */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
            <h5 className="font-semibold text-[var(--color-surface-text)] mb-3">
              {t('ai.roadmapGenerator.saveSection.title')}
            </h5>
            <p className="text-[var(--color-surface-text)] mb-4">
              {t('ai.roadmapGenerator.saveSection.description')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => handleSaveRoadmap('tenant')}
                disabled={isSaving}
                className="px-4 py-2 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving ? t('ai.roadmapGenerator.roadmap.saving') : t('ai.roadmapGenerator.roadmap.saveToMyRoadmaps')}
              </button>
              <button
                onClick={() => handleSaveRoadmap('marketplace')}
                disabled={isSaving}
                className="px-4 py-2 bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-primary-bg)] hover:text-[var(--color-primary-text)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving ? t('ai.roadmapGenerator.roadmap.saving') : t('ai.roadmapGenerator.roadmap.publishToMarketplace')}
              </button>
            </div>
            <p className="text-sm text-[var(--color-surface-text)] opacity-80 mt-2">
              <strong>{t('ai.roadmapGenerator.saveSection.myRoadmapsDescription')}</strong><br/>
              <strong>{t('ai.roadmapGenerator.saveSection.marketplaceDescription')}</strong>
            </p>
          </div>

          {/* Usage Information */}
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
            <h5 className="font-semibold text-[#004D99] mb-2">
              {t('ai.roadmapGenerator.usageInfo')}
            </h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium">{t('ai.roadmapGenerator.usage.model')}:</span> {result.usage.model}
              </div>
              <div>
                <span className="font-medium">{t('ai.roadmapGenerator.usage.tokens')}:</span> {result.usage.inputTokens + result.usage.outputTokens}
              </div>
              <div>
                <span className="font-medium">{t('ai.roadmapGenerator.usage.cost')}:</span> ${result.usage.cost.toFixed(4)}
              </div>
              <div>
                <span className="font-medium">{t('ai.roadmapGenerator.usage.time')}:</span> {(result.usage.responseTime / 1000).toFixed(1)}s
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      )}

      {/* Business Problem Solver Tab */}
      {activeTab === 'problem-solver' && (
        <div>
          <div className="mb-6">
            <h3 className="text-xl font-bold text-[#004D99] mb-2">
              {t('ai.roadmapGenerator.businessProblemSolver.title')}
            </h3>
            <p className="text-[#95C4FF]">
              {t('ai.roadmapGenerator.businessProblemSolver.description')}
            </p>
          </div>

          {/* Business Context Form */}
          <div className="space-y-4 mb-6">
            {/* First row: Industry, Size, Revenue, and Employees */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  {t('ai.roadmapGenerator.businessProblemSolver.businessContext.industry')}
                </label>
                <input
                  type="text"
                  value={businessContext.industry}
                  onChange={(e) => setBusinessContext({...businessContext, industry: e.target.value})}
                  placeholder={t('ai.roadmapGenerator.businessProblemSolver.businessContext.industryPlaceholder')}
                  className="w-full p-3 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-primary-bg)] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  {t('ai.roadmapGenerator.businessProblemSolver.businessContext.companySize')}
                </label>
                <select
                  value={businessContext.companySize}
                  onChange={(e) => setBusinessContext({...businessContext, companySize: e.target.value})}
                  className="w-full p-3 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-primary-bg)] focus:border-transparent"
                >
                  <option value="">{t('ai.roadmapGenerator.businessProblemSolver.selectSize')}</option>
                  <option value="startup">{t('ai.roadmapGenerator.businessProblemSolver.companySizeOptions.startup')}</option>
                  <option value="small">{t('ai.roadmapGenerator.businessProblemSolver.companySizeOptions.small')}</option>
                  <option value="medium">{t('ai.roadmapGenerator.businessProblemSolver.companySizeOptions.medium')}</option>
                  <option value="large">{t('ai.roadmapGenerator.businessProblemSolver.companySizeOptions.large')}</option>
                  <option value="enterprise">{t('ai.roadmapGenerator.businessProblemSolver.companySizeOptions.enterprise')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  {t('ai.roadmapGenerator.businessProblemSolver.businessContext.revenue')}
                </label>
                <input
                  type="text"
                  value={businessContext.revenue}
                  onChange={(e) => setBusinessContext({...businessContext, revenue: e.target.value})}
                  placeholder={t('ai.roadmapGenerator.businessProblemSolver.businessContext.revenuePlaceholder')}
                  className="w-full p-3 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-primary-bg)] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  {t('ai.roadmapGenerator.businessProblemSolver.businessContext.employees')}
                </label>
                <input
                  type="text"
                  value={businessContext.employees}
                  onChange={(e) => setBusinessContext({...businessContext, employees: e.target.value})}
                  placeholder={t('ai.roadmapGenerator.businessProblemSolver.businessContext.employeesPlaceholder')}
                  className="w-full p-3 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-primary-bg)] focus:border-transparent"
                />
              </div>
            </div>

            {/* Second row: Business Challenge, Goals, Timeline, and Budget */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  {t('ai.roadmapGenerator.businessProblemSolver.businessContext.currentChallenges')}
                </label>
                <textarea
                  value={businessContext.currentChallenges}
                  onChange={(e) => setBusinessContext({...businessContext, currentChallenges: e.target.value})}
                  placeholder={t('ai.roadmapGenerator.businessProblemSolver.businessContext.currentChallengesPlaceholder')}
                  className="w-full p-3 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-primary-bg)] focus:border-transparent h-20 resize-none"
                  disabled={isAnalyzing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  {t('ai.roadmapGenerator.businessProblemSolver.businessContext.goals')}
                </label>
                <textarea
                  value={businessContext.goals}
                  onChange={(e) => setBusinessContext({...businessContext, goals: e.target.value})}
                  placeholder={t('ai.roadmapGenerator.businessProblemSolver.businessContext.goalsPlaceholder')}
                  className="w-full p-3 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-primary-bg)] focus:border-transparent h-20 resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  {t('ai.roadmapGenerator.businessProblemSolver.businessContext.timeline')}
                </label>
                <input
                  type="text"
                  value={businessContext.timeline}
                  onChange={(e) => setBusinessContext({...businessContext, timeline: e.target.value})}
                  placeholder={t('ai.roadmapGenerator.businessProblemSolver.businessContext.timelinePlaceholder')}
                  className="w-full p-3 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-primary-bg)] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  {t('ai.roadmapGenerator.businessProblemSolver.businessContext.budget')}
                </label>
                <input
                  type="text"
                  value={businessContext.budget}
                  onChange={(e) => setBusinessContext({...businessContext, budget: e.target.value})}
                  placeholder={t('ai.roadmapGenerator.businessProblemSolver.businessContext.budgetPlaceholder')}
                  className="w-full p-3 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:ring-2 focus:ring-[var(--color-primary-bg)] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={handleStartConversation}
              disabled={isAnalyzing || !businessContext.currentChallenges.trim()}
              className="px-6 py-2 bg-[#004C94] text-white rounded-md hover:bg-[#004D99] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isAnalyzing ? t('ai.roadmapGenerator.businessProblemSolver.startingConversation') : t('ai.roadmapGenerator.businessProblemSolver.startConversation')}
            </button>
          </div>

          {/* Analysis Status Messages */}
          {isAnalyzing && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#004C94] mr-3"></div>
                <div>
                  <p className="text-[#004C94] font-medium">{t('ai.roadmapGenerator.businessProblemSolver.analysisStatus.analyzing')}</p>
                  <p className="text-gray-600 text-sm mt-1">{t('ai.roadmapGenerator.businessProblemSolver.analysisStatus.analyzingDescription')}</p>
                </div>
              </div>
            </div>
          )}


          {/* Natural Conversation Interface */}
          {conversationStep === 'conversation' && (
            <div className="mb-6">
              {/* Conversation History */}
              <div className="mb-6 max-h-96 overflow-y-auto space-y-4 p-4 bg-gray-50 rounded-md">
                {conversationHistory.map((item, index) => (
                  <div key={index} className={`flex ${item.type === 'ai' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      item.type === 'ai' 
                        ? 'bg-blue-100 text-blue-900' 
                        : 'bg-[#004C94] text-white'
                    }`}>
                      <div className="flex items-start">
                        <span className="text-sm mr-2">
                          {item.type === 'ai' ? '🤖' : '👤'}
                        </span>
                        <p className="text-sm">{item.content}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Current AI Question */}
              {currentQuestion && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-start">
                    <span className="text-xl mr-3">🤖</span>
                    <div className="flex-1">
                      <p className="text-gray-700 mb-3">{currentQuestion}</p>
                      
                      {/* User Answer Input */}
                      <div className="flex gap-2">
                        <textarea
                          value={userAnswer}
                          onChange={(e) => setUserAnswer(e.target.value)}
                          placeholder={t('ai.roadmapGenerator.businessProblemSolver.conversation.answerPlaceholder')}
                          className="flex-1 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#5B94DD] focus:border-transparent resize-none"
                          rows={2}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                        />
                        <button
                          onClick={handleSendMessage}
                          disabled={!userAnswer.trim() || isGeneratingResponse}
                          className="px-4 py-2 bg-[#004C94] text-white rounded-md hover:bg-[#004D99] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {t('ai.roadmapGenerator.businessProblemSolver.conversation.sendButton')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* AI is thinking */}
              {isGeneratingResponse && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600 mr-3"></div>
                    <p className="text-yellow-800 font-medium">{t('ai.roadmapGenerator.businessProblemSolver.conversation.aiThinking')}</p>
                  </div>
                </div>
              )}

              {/* Reset Conversation Button */}
              <div className="text-center">
                <button
                  onClick={() => {
                    setConversationStep('input');
                    setConversationHistory([]);
                    setCurrentQuestion('');
                    setUserAnswer('');
                    setError(null);
                  }}
                  className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  {t('ai.roadmapGenerator.businessProblemSolver.conversation.startNewConversation')}
                </button>
              </div>
            </div>
          )}

          {/* AI Suggestions */}
          {conversationStep === 'suggestions' && suggestedOptions && (
            <div className="mb-6 p-6 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-start mb-4">
                <span className="text-2xl mr-3">💡</span>
                <div>
                  <h4 className="text-lg font-bold text-green-800 mb-2">
                    {t('ai.roadmapGenerator.businessProblemSolver.conversation.aiRecommendations')}
                  </h4>
                  <p className="text-green-700 mb-4">{suggestedOptions.summary}</p>
                </div>
              </div>
              
              <div className="mb-4">
                <h5 className="font-medium text-green-800 mb-2">{t('ai.roadmapGenerator.businessProblemSolver.conversation.recommendedTransformations')}</h5>
                <ul className="space-y-2">
                  {suggestedOptions.recommendations.map((rec, index) => (
                    <li key={index} className="text-green-700 text-sm">
                      • {rec}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={handleBuildProjects}
                  disabled={isGenerating}
                  className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isGenerating ? t('ai.roadmapGenerator.businessProblemSolver.suggestions.buildingProjects') : t('ai.roadmapGenerator.businessProblemSolver.suggestions.buildProjects')}
                </button>
                <button
                  onClick={() => {
                    setConversationStep('conversation');
                    setSuggestedOptions(null);
                  }}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  {t('ai.roadmapGenerator.businessProblemSolver.suggestions.continueConversation')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
};


